import React from 'react';
import { Box, Typography, Avatar, Chip } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { CalendarToday, LocationOn, AccessTime } from '@mui/icons-material';
import spacing from '../../design-system/spacing';
import shadows from '../../design-system/shadows';

/**
 * MatchCard - Card de partido con equipos, resultado, fecha
 * Para mostrar información de partidos (pasados y futuros)
 */

const StyledCard = styled(motion.div, {
  shouldForwardProp: (prop) => prop !== 'isLive',
})(({ isLive, theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: spacing.borderRadius.lg,
  padding: spacing[3],
  boxShadow: shadows.card,
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  border: isLive ? `2px solid ${theme.palette.error.main}` : `1px solid ${theme.palette.divider}`,
  position: 'relative',
  
  '&:hover': {
    boxShadow: shadows.cardHover,
    transform: 'translateY(-2px)',
    borderColor: isLive ? theme.palette.error.main : theme.palette.primary.light,
  },
}));

const ScoreBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isWinner',
})(({ isWinner, theme }) => ({
  minWidth: 48,
  height: 48,
  borderRadius: spacing.borderRadius.md,
  backgroundColor: isWinner ? theme.palette.primary.main : theme.palette.action.selected,
  color: isWinner ? theme.palette.primary.contrastText : theme.palette.text.primary,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: 20,
}));

const MatchCard = ({
  equipoLocal,
  escudoLocal,
  golesLocal,
  equipoVisitante,
  escudoVisitante,
  golesVisitante,
  fecha,
  hora,
  estadio,
  competicion,
  categoria,
  jornada,
  estado = 'finalizado', // 'finalizado', 'en-vivo', 'programado'
  onClick,
}) => {
  const theme = useTheme();
  const isLive = estado === 'en-vivo';
  const isProgramado = estado === 'programado';
  const isFinalizado = estado === 'finalizado';

  const localGano = golesLocal > golesVisitante;
  const visitanteGano = golesVisitante > golesLocal;
  const empate = golesLocal === golesVisitante && isFinalizado;

  // Handler para errores de carga de imagen
  const handleImageError = (e) => {
    e.target.style.display = 'none'; // Ocultar imagen rota
  };

  // Formatear fecha - corregido para manejar diferentes formatos
  const formatFecha = (fechaStr) => {
    if (!fechaStr) return '';
    
    // Si la fecha viene en formato DD/MM/YYYY, convertir a YYYY-MM-DD
    let dateToFormat;
    if (fechaStr.includes('/')) {
      const [day, month, year] = fechaStr.split('/');
      dateToFormat = new Date(`${year}-${month}-${day}`);
    } else {
      dateToFormat = new Date(fechaStr);
    }
    
    // Verificar si la fecha es válida
    if (isNaN(dateToFormat.getTime())) {
      return fechaStr; // Retornar el string original si no se puede parsear
    }
    
    return dateToFormat.toLocaleDateString('es-ES', { 
      weekday: 'short', 
      day: '2-digit', 
      month: 'short' 
    });
  };

  return (
    <StyledCard
      isLive={isLive}
      onClick={onClick}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Estado del partido */}
      {isLive && (
        <Chip
          label="EN VIVO"
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: theme.palette.error.main,
            color: theme.palette.error.contrastText,
            fontWeight: 700,
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.7 },
            },
          }}
        />
      )}

      {/* Header con competición y jornada */}
      {(competicion || categoria || jornada) && (
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {categoria && (
            <Chip
              label={categoria}
              size="small"
              sx={{
                backgroundColor: theme.palette.action.selected,
                color: theme.palette.primary.main,
                fontSize: 11,
                fontWeight: 600,
              }}
            />
          )}
          {competicion && (
            <Chip
              label={competicion}
              size="small"
              sx={{
                backgroundColor: theme.palette.action.hover,
                color: theme.palette.primary.main,
                fontSize: 11,
              }}
            />
          )}
          {jornada && (
            <Chip
              label={`Jornada ${jornada}`}
              size="small"
              sx={{
                backgroundColor: theme.palette.action.selected,
                color: theme.palette.text.secondary,
                fontSize: 11,
              }}
            />
          )}
        </Box>
      )}

      {/* Equipos y resultado - NUEVO DISEÑO VERTICAL */}
      <Box sx={{ mb: 2 }}>
        {/* Fila 1: Escudos de ambos equipos */}
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-around',
            alignItems: 'center',
            mb: 2,
            gap: 3,
          }}
        >
          {/* Escudo Local */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
            <Avatar
              src={escudoLocal || undefined}
              alt={equipoLocal}
              imgProps={{
                onError: handleImageError
              }}
              sx={{
                width: 60,
                height: 60,
                backgroundColor: theme.palette.action.selected,
                color: theme.palette.text.primary,
                fontSize: 24,
                fontWeight: 700,
              }}
            >
              {equipoLocal?.substring(0, 2).toUpperCase()}
            </Avatar>
          </Box>

          {/* VS o Resultado central */}
          {isProgramado ? (
            <Box
              sx={{
                px: 3,
                py: 1.5,
                backgroundColor: theme.palette.action.selected,
                borderRadius: spacing.borderRadius.md,
              }}
            >
              <Typography variant="h5" fontWeight={700} color={theme.palette.text.secondary}>
                VS
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <ScoreBox isWinner={localGano && !empate}>
                {golesLocal ?? '-'}
              </ScoreBox>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 1,
                  color: theme.palette.text.secondary,
                  fontWeight: 700,
                  fontSize: 18,
                }}
              >
                −
              </Box>
              <ScoreBox isWinner={visitanteGano && !empate}>
                {golesVisitante ?? '-'}
              </ScoreBox>
            </Box>
          )}

          {/* Escudo Visitante */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
            <Avatar
              src={escudoVisitante || undefined}
              alt={equipoVisitante}
              imgProps={{
                onError: handleImageError
              }}
              sx={{
                width: 60,
                height: 60,
                backgroundColor: theme.palette.action.selected,
                color: theme.palette.text.primary,
                fontSize: 24,
                fontWeight: 700,
              }}
            >
              {equipoVisitante?.substring(0, 2).toUpperCase()}
            </Avatar>
          </Box>
        </Box>

        {/* Fila 2: Nombres de equipos */}
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-around',
            alignItems: 'flex-start',
            gap: 3,
            mb: 1,
          }}
        >
          {/* Nombre Local */}
          <Box sx={{ flex: 1, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography
              variant="body2"
              fontWeight={localGano ? 700 : 600}
              sx={{
                color: theme.palette.text.primary,
                mb: 0.5,
                lineHeight: 1.2,
              }}
            >
              {equipoLocal}
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                color: theme.palette.text.secondary,
                textTransform: 'uppercase',
                fontSize: 10,
                fontWeight: 600,
              }}
            >
              Local
            </Typography>
          </Box>

          {/* Espacio central (para mantener alineación con el resultado) */}
          <Box sx={{ minWidth: isProgramado ? 80 : 120 }} />

          {/* Nombre Visitante */}
          <Box sx={{ flex: 1, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography
              variant="body2"
              fontWeight={visitanteGano ? 700 : 600}
              sx={{
                color: theme.palette.text.primary,
                mb: 0.5,
                lineHeight: 1.2,
              }}
            >
              {equipoVisitante}
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                color: theme.palette.text.secondary,
                textTransform: 'uppercase',
                fontSize: 10,
                fontWeight: 600,
              }}
            >
              Visitante
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Info del partido */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
          pt: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        {fecha && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CalendarToday sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
            <Typography variant="caption" color={theme.palette.text.secondary}>
              {formatFecha(fecha)}
            </Typography>
          </Box>
        )}
        {hora && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTime sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
            <Typography variant="caption" color={theme.palette.text.secondary}>
              {hora}
            </Typography>
          </Box>
        )}
        {estadio && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LocationOn sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
            <Typography
              variant="caption"
              color={theme.palette.text.secondary}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {estadio}
            </Typography>
          </Box>
        )}
      </Box>
    </StyledCard>
  );
};

export default MatchCard;
